import React, { useState, useEffect } from 'react';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import {
  PencilSimple,
  SpinnerGap,
  Check,
  Image as ImageIcon,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditRestaurantModal = ({ open, onOpenChange, restaurant, onSuccess }) => {
  const { token } = useAuth();
  const api = createApiClient(token);

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [suburb, setSuburb] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [description, setDescription] = useState('');
  const [bookingLink, setBookingLink] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [corkage, setCorkage] = useState('');
  const [selectedCover, setSelectedCover] = useState('');

  // Collect all photo URLs from visits
  const allPhotos = [];
  (restaurant?.visits || []).forEach(visit => {
    (visit.attachments || []).forEach(att => {
      const url = att.url
        ? `${BACKEND_URL}${att.url.startsWith('/api') ? '' : '/api'}${att.url}`
        : att.file_path
          ? `${BACKEND_URL}/api${att.file_path}`
          : null;
      if (url) allPhotos.push(url);
    });
  });

  useEffect(() => {
    if (open && restaurant) {
      setName(restaurant.name || '');
      setSuburb(restaurant.suburb || '');
      setCuisine(restaurant.cuisine?.join(', ') || '');
      setPriceRange(restaurant.price_range || '');
      setDescription(restaurant.description || '');
      setBookingLink(restaurant.booking_link || '');
      setWebsiteUrl(restaurant.website_url || '');
      setInstagramUrl(restaurant.instagram_url || '');
      setCorkage(restaurant.corkage || '');
      setSelectedCover(restaurant.hero_image_url || '');
    }
  }, [open, restaurant]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Restaurant name is required');
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        name: name.trim(),
        suburb: suburb.trim() || null,
        cuisine: cuisine ? cuisine.split(',').map(c => c.trim()).filter(Boolean) : [],
        price_range: priceRange || null,
        description: description.trim() || null,
        booking_link: bookingLink.trim() || null,
        website_url: websiteUrl.trim() || null,
        instagram_url: instagramUrl.trim() || null,
        corkage: corkage.trim() || null,
      };

      const response = await api.put(`/restaurants/${restaurant.id}`, updateData);

      // Update cover image if changed
      if (selectedCover !== (restaurant.hero_image_url || '')) {
        await api.put(`/restaurants/${restaurant.id}/cover-image`, { image_url: selectedCover || null });
      }

      toast.success('Restaurant updated!');
      if (onSuccess) onSuccess(response.data);
      onOpenChange(false);
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update restaurant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto" data-testid="edit-restaurant-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <PencilSimple weight="duotone" className="w-6 h-6 text-[#A94438]" />
            Edit Restaurant
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-name">Name *</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5"
              data-testid="edit-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-suburb">Suburb</Label>
              <Input
                id="edit-suburb"
                value={suburb}
                onChange={(e) => setSuburb(e.target.value)}
                className="mt-1.5"
                data-testid="edit-suburb-input"
              />
            </div>
            <div>
              <Label>Price Range</Label>
              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className="mt-1.5" data-testid="edit-price-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="budget">Budget (R)</SelectItem>
                  <SelectItem value="mid">Mid-range (RR)</SelectItem>
                  <SelectItem value="premium">Premium (RRR)</SelectItem>
                  <SelectItem value="luxury">Luxury (RRRR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-cuisine">Cuisine (comma separated)</Label>
            <Input
              id="edit-cuisine"
              placeholder="e.g., Italian, Mediterranean"
              value={cuisine}
              onChange={(e) => setCuisine(e.target.value)}
              className="mt-1.5"
              data-testid="edit-cuisine-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1.5"
              rows={3}
              data-testid="edit-desc-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-booking">Booking Link</Label>
            <Input
              id="edit-booking"
              placeholder="https://..."
              value={bookingLink}
              onChange={(e) => setBookingLink(e.target.value)}
              className="mt-1.5"
              data-testid="edit-booking-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-website">Website</Label>
            <Input
              id="edit-website"
              placeholder="https://..."
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="mt-1.5"
              data-testid="edit-website-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-instagram">Instagram</Label>
            <Input
              id="edit-instagram"
              placeholder="https://instagram.com/..."
              value={instagramUrl}
              onChange={(e) => setInstagramUrl(e.target.value)}
              className="mt-1.5"
              data-testid="edit-instagram-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-corkage">Corkage Info</Label>
            <Input
              id="edit-corkage"
              placeholder="e.g., R120 per bottle"
              value={corkage}
              onChange={(e) => setCorkage(e.target.value)}
              className="mt-1.5"
              data-testid="edit-corkage-input"
            />
          </div>

          {/* Cover Image Picker */}
          {allPhotos.length > 0 && (
            <div>
              <Label className="flex items-center gap-1.5 mb-2">
                <ImageIcon weight="duotone" className="w-4 h-4" />
                Cover Image
              </Label>
              <div className="grid grid-cols-4 gap-2 max-h-[160px] overflow-y-auto rounded-lg p-1 bg-[#F9F8F5]" data-testid="cover-image-picker">
                {allPhotos.map((url, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedCover(url)}
                    className={`aspect-square rounded-lg overflow-hidden ring-2 transition-all ${
                      selectedCover === url
                        ? 'ring-[#C4A35A] ring-offset-1'
                        : 'ring-transparent hover:ring-[#EAE8E3]'
                    }`}
                    data-testid={`cover-option-${i}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
              {selectedCover && (
                <button
                  type="button"
                  onClick={() => setSelectedCover('')}
                  className="text-xs text-[#9A9590] hover:text-[#A94438] mt-1"
                  data-testid="clear-cover-btn"
                >
                  Remove cover image
                </button>
              )}
            </div>
          )}

          <Button
            onClick={handleSave}
            className="w-full btn-primary"
            disabled={loading}
            data-testid="save-edit-btn"
          >
            {loading ? (
              <SpinnerGap className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check weight="bold" className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditRestaurantModal;
